import axios from "axios";
import { useState } from "react";
import { useGetLoggedInUserInfoQuery } from "../repositories/queries/user/get-users-info.query";
import { ILoginResult, LoginOperationResult, setSession } from "../utils/auth";
import { useLoginQuery } from "../repositories/queries/auth/mutation/login.query";

export const EMAIL = "email";
export const PASSWORD = "password";

interface IAuthLogin {
  email: string;
  password: string;
}

const initialValue: IAuthLogin = {
  email: "",
  password: "",
};

export const useLogin = () => {
  const [authLogin, setAuthLoginLogin] = useState<IAuthLogin>(initialValue);
  const { refetch: refetchUserInfo } = useGetLoggedInUserInfoQuery(false);
  const { mutateAsync: login } = useLoginQuery();
  const handleLogin = async (): Promise<ILoginResult> => {
    return await handleAuthentication(authLogin.email, authLogin.password);
  };

  const setValue = (value: string, id: keyof IAuthLogin) => {
    const copyAuth = JSON.parse(JSON.stringify(authLogin));
    copyAuth[id] = value;
    setAuthLoginLogin(copyAuth);
  };

  const handleAuthentication = async (email: string, password: string): Promise<ILoginResult> => {
    try {
      const authResponse = await login({ email, password });

      if (authResponse.status === 401) {
        return { result: LoginOperationResult.WrongLoginData };
      }

      if (authResponse.status === 200) {
        const token = authResponse.result.token ?? "";

        setSession(token);
        const userResponse = await refetchUserInfo();

        if (userResponse.data?.status === 401) {
          return { result: LoginOperationResult.WrongLoginData };
        }

        if (userResponse.data?.status === 200) {
          return {
            result: LoginOperationResult.Success,
            token,
            user: userResponse.data.user,
          };
        }
      }

      return { result: LoginOperationResult.UnexpectedError };
    } catch (e) {
      if (axios.isAxiosError(e)) {
        if (e.response?.status === 403) {
          return { result: LoginOperationResult.NoContracts };
        }

        if (e.response?.status === 401) {
          return { result: LoginOperationResult.WrongLoginData };
        }

        if (e.response?.status === 429) {
          return { result: LoginOperationResult.TooManyRequest };
        }
      }

      return { result: LoginOperationResult.UnexpectedError };
    }
  };

  return [authLogin, setValue, handleLogin] as const;
};
